import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BalanceReportComputedRowFormDto, type BalanceReportComputedRowFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-format/balance-report-computed-row-form.dto';
import type { BalanceReportRowTokenInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-custom-row/balance-report-row-token.dto';
import { BalanceReportTypeEnum } from '@zaoblako/shared/modules/finance/enums/balance-report-type.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { BalanceReportFormatService } from '@zaoblako/web/core/services/finance/balance-report-format.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import { TreeNode } from 'primeng/api';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { MessagesModule } from 'primeng/messages';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { TreeModule } from 'primeng/tree';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-balance-custom-row-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    ButtonComponent,
    InputTextComponent,
    JoinPipe,
    CardModule,
    MessagesModule,
    OverlayPanelModule,
    OrderByPipe,
    CheckboxModule,
    FormsModule,
    TreeModule
  ],
  templateUrl: './balance-custom-row-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BalanceCustomRowFormComponent
  extends BaseFormComponent<BalanceReportComputedRowFormInterface>
  implements OnInit {
  @ViewChild('expressionInput') public expressionInput: InputTextComponent;
  @ViewChild('opExpression') public opExpression: OverlayPanel;

  @Input() public readonly index: number;

  public form: BalanceReportComputedRowFormInterface = {
    formula: '',
    name: ''
  };

  /** Report type from sidebar data — used for token filtering and CSS, not validated */
  public reportType: BalanceReportTypeEnum | null = null;

  public dto = BalanceReportComputedRowFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public isShowOverlayPanel = false;
  public resultExpression = '';

  public customRows: TreeNode<BalanceReportRowTokenInterface>[] = [];

  public get BalanceReportTypeEnum() {
    return BalanceReportTypeEnum;
  }

  public constructor(
    private readonly balanceReportFormatService: BalanceReportFormatService,
    private readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.formStoreService.setLoading(true, this.index);
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    const formData = this.formStoreService.getState.forms[this.index]?.data;
    if (formData?.type) {
      this.reportType = formData.type;
    }

    const formatId = formData?.formatId as number | undefined;
    if (!formatId) {
      this.formStoreService.setLoading(false, this.index);
      this.errorService.addCustomError('Ошибка', 'Не указана структура отчёта');
      return;
    }

    this.balanceReportFormatService.getTokens(formatId).subscribe({
      next: (customRows) => {
        const parseChildCustomRow = (rows: BalanceReportRowTokenInterface[]): TreeNode[] => {
          const result: TreeNode[] = [];
          for (let row of rows) {
            if (row.type && row.type !== this.reportType) {
              continue;
            }

            const node: TreeNode = {
              label: row.name,
              selectable: true,
              key: row.key,
              expanded: true,
              children: row.children?.length ? parseChildCustomRow(row.children) : [],
              data: row
            };
            result.push(node);
          }
          return result;
        };

        this.customRows = parseChildCustomRow(customRows);
        this.cdRef.markForCheck();
        if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
          this.formStoreService.setLoading(false, this.index);
        }
        this.parseExpression();
      }
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);
    const formatId = this.formStoreService.getState.forms[this.index]?.data?.formatId as number | undefined;
    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;

    if (!formatId) {
      this.formStoreService.setLoading(false, this.index);
      this.errorService.addCustomError('Ошибка', 'Не указана структура отчёта');
      return;
    }

    this.balanceReportFormatService.findOneComputedRow(formatId, entityId).subscribe({
      next: (item: { name: string; expression?: string; formula?: string; measure?: string; color?: string }) => {
        this.form = {
          ...this.form,
          name: item.name,
          formula: item.formula || item.expression || '',
          measure: item.measure,
          color: item.color
        };
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
        this.parseExpression();
      }
    });
  }

  public saveItem() {
    if (this.checkValidate(this.form).valid) {
      const formatId = this.formStoreService.getState.forms[this.index]?.data?.formatId as number | undefined;
      const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
      const payload: BalanceReportComputedRowFormInterface = {
        name: this.form.name,
        formula: this.form.formula,
        measure: this.form.measure,
        color: this.form.color
      };

      if (!formatId) {
        this.errorService.addCustomError('Ошибка', 'Не указана структура отчёта');
        return;
      }

      if (entityId) {
        this.formStoreService.setLoading(true, this.index);
        this.balanceReportFormatService.updateComputedRow(formatId, entityId, payload).subscribe({
          next: (item) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Фин.показатель "${this.form?.name}" обновлен`);
            this.formStoreService.saveNext(item, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);
        this.balanceReportFormatService.createComputedRow(formatId, payload).subscribe({
          next: (item) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Фин.показатель создан');
            this.formStoreService.saveNext(item, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCustomRow() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить Фин.показатель "${this.form?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();
        const formatId = this.formStoreService.getState.forms[this.index]?.data?.formatId as number | undefined;
        const entityId = this.formStoreService.getState.forms[this.index]?.entityId;

        if (!formatId) {
          this.formStoreService.setLoading(false, this.index);
          this.errorService.addCustomError('Ошибка', 'Не указана структура отчёта');
          return;
        }

        this.balanceReportFormatService.deleteComputedRow(formatId, entityId).subscribe(() => {
          this.errorService.addSuccessMessage(`Фин.показатель "${this.form?.name}" удален`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public togglePercent() {
    if (this.form.measure === '%') {
      this.form.measure = '';
    } else {
      this.form.measure = '%';
    }
  }

  public toggleColor() {
    if (this.form.color === '#F4F4F5') {
      this.form.color = null;
    } else {
      this.form.color = '#F4F4F5';
    }
  }

  public selectCustomRow(customRow: TreeNode<BalanceReportRowTokenInterface>) {
    const selectionStart = (this.expressionInput?.input?.nativeElement as HTMLInputElement)?.selectionStart;
    if (!isNaN(selectionStart)) {
      if (!this.form.formula) {
        this.form.formula = '';
      }
      this.form.formula =
        this.form.formula.slice(0, selectionStart) + customRow.key + this.form.formula.slice(selectionStart);
      (this.expressionInput?.input?.nativeElement as HTMLInputElement)?.focus();
      setTimeout(() => {
        (this.expressionInput?.input?.nativeElement as HTMLInputElement)?.setSelectionRange(
          selectionStart + customRow.key?.length,
          selectionStart + customRow.key?.length
        );
      });
      this.parseExpression();
    }
  }

  public parseExpression() {
    const flattenArray = (rows: TreeNode<BalanceReportRowTokenInterface>[]) => {
      let result: TreeNode<BalanceReportRowTokenInterface>[] = [];
      rows.forEach((row) => {
        result.push(row);
        if (row.children) {
          result = result.concat(flattenArray(row.children));
        }
      });
      return result;
    };

    let expression = this.form.formula || '';
    const customRows = [...flattenArray(this.customRows)].sort((a, b) => (a.key?.length > b.key?.length ? -1 : 1));
    customRows.forEach((customRow) => {
      if (expression.includes(customRow.key)) {
        expression = expression.replace(customRow.key, `"${customRow.label}"`);
      }
    });
    this.resultExpression = expression;
  }

  public keydownEscInExpressionInput(event: KeyboardEvent) {
    if (this.isShowOverlayPanel) {
      this.opExpression.hide();
      event.preventDefault();
      event.stopPropagation();
    }
  }
}
